import React, { useState, useRef } from 'react';
import { TextField, TextAreaField, RadioField, CheckboxGroup, SelectField, SectionHeader } from './FormField';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { Upload, Trash2, Loader2, AlertCircle } from 'lucide-react';
import ChargeSelector from '@/components/aid/ChargeSelector';
import { COURT_TYPE_OPTIONS, COURT_LOCATIONS_BY_TYPE } from '@/lib/courtLocations';

const toOptions = (list) => list.map((v) => ({ value: v, label: v }));

// The PDF and printable form read court_tribunal, so keep it as the combined answer.
const courtTribunalText = ({ court_type, court_location, court_type_other }) => {
  if (court_type === "Other") return court_type_other || "";
  return [court_type, court_location].filter(Boolean).join(" – ");
};

const ROLE_OPTIONS = [
  { value: "Applicant/plaintiff/appellant", label: "Applicant/plaintiff/appellant" },
  { value: "Accused/defendant/respondent", label: "Accused/defendant/respondent" },
  { value: "Interested party", label: "Interested party" },
  { value: "No court proceedings", label: "No court proceedings" },
  { value: "Other", label: "Other" },
];

const HEARING_TYPE_OPTIONS = [
  { value: "Appeal", label: "Appeal" }, { value: "Summary hearing", label: "Summary hearing" },
  { value: "Indictable hearing", label: "Indictable hearing" },
  { value: "Family law hearing", label: "Family law hearing" },
  { value: "Civil application", label: "Civil application" },
  { value: "Mediation/ADR", label: "Mediation/alternative dispute resolution (ADR)" },
  { value: "No hearing", label: "No hearing" }, { value: "Other", label: "Other" },
];

const LAW_TYPE_OPTIONS = [
  { value: "Criminal law", label: "Criminal law" },
  { value: "Family law", label: "Family law" },
  { value: "Family violence", label: "Family violence" },
  { value: "Civil law", label: "Civil law" },
];

const FACTOR_OPTIONS = [
  { value: "fv_victim", label: "Family violence – victim/survivor" },
  { value: "fv_perpetrator", label: "Family violence – alleged perpetrator" },
  { value: "covid", label: "COVID-19" }, { value: "ice", label: "Ice" },
  { value: "alcohol", label: "Alcohol" }, { value: "drugs_other", label: "Drugs – other" },
];

export default function Step7Court({ data, onChange }) {
  const courtLocations = COURT_LOCATIONS_BY_TYPE[data.court_type] || [];

  const handleCourtChange = (name, value) => {
    const next = { ...data, [name]: value };
    if (name === "court_type") {
      // Drop a location the new court doesn't sit at; pick it when there's only one.
      const locations = COURT_LOCATIONS_BY_TYPE[value] || [];
      next.court_location = locations.length === 1 ? locations[0] : locations.includes(data.court_location) ? data.court_location : "";
      onChange("court_location", next.court_location);
    }
    onChange(name, value);
    onChange("court_tribunal", courtTribunalText(next));
  };

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      onChange("fees_proof_attachment", file_url);
      onChange("fees_proof_attachment_name", file.name);
    } catch (err) {
      console.error("Error uploading file:", err);
      setUploadError("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = () => {
    onChange("fees_proof_attachment", "");
    onChange("fees_proof_attachment_name", "");
    setUploadError(null);
  };
  return (
    <div className="space-y-8">
      <div>
        <SectionHeader number="16" title="Court hearings" />
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 space-y-1">
          <p className="font-medium">Search for court listings:</p>
          <a href="https://cmsportal.courts.vic.gov.au/?q=hearings" target="_blank" rel="noopener noreferrer" className="block underline hover:text-blue-600 font-semibold">
            Court Services Victoria CMS - Search hearings
          </a>
          <a href="https://eappearance.countycourt.vic.gov.au/courtconnect" target="_blank" rel="noopener noreferrer" className="block underline hover:text-blue-600 font-semibold">
            County Court of Victoria - Case search
          </a>
          <ul className="hidden">
            <li>
              <a href="https://cmsportal.courts.vic.gov.au/?q=hearings" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                Magistrates' Court Daily Lists – Case Search
              </a>
            </li>
            <li>
              <a href="http://cjep.justice.vic.gov.au/pls/p100/ck_public_qry_crim.cp_crimlist_setup_idx" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                County Court List
              </a>
            </li>
          </ul>
        </div>
        <div className="space-y-5">
          <RadioField label="Are there any proceedings?" name="has_proceedings" value={data.has_proceedings} onChange={onChange} options={[{value:"No",label:"No"},{value:"Yes, current",label:"Yes, current"},{value:"Yes, intended",label:"Yes, intended"}]} />
          {data.has_proceedings !== "No" && data.has_proceedings && (
            <>
              <TextField label="When is the next hearing date?" name="hearing_date" value={data.hearing_date} onChange={onChange} type="date" />
              <SelectField label="Which court/tribunal do you have to go to?" name="court_type" value={data.court_type} onChange={handleCourtChange} options={toOptions(COURT_TYPE_OPTIONS)} placeholder="Select court type..." />
              {data.court_type === "Other" ? (
                <TextField label="Please specify the court or tribunal" name="court_type_other" value={data.court_type_other} onChange={handleCourtChange} />
              ) : data.court_type ? (
                <SelectField label="Court location" name="court_location" value={data.court_location} onChange={handleCourtChange} options={toOptions(courtLocations)} placeholder="Select location..." />
              ) : null}
              {!data.court_type && data.court_tribunal && (
                <p className="text-xs text-gray-500">Previously entered: {data.court_tribunal}</p>
              )}
              <RadioField label="What is your role in these proceedings?" name="proceedings_role" value={data.proceedings_role} onChange={onChange} options={ROLE_OPTIONS} inline={false} />
              <RadioField label="What type of hearing is it?" name="hearing_type" value={data.hearing_type} onChange={onChange} options={HEARING_TYPE_OPTIONS} inline={false} />
              {data.hearing_type === "Other" && (
                <TextField label="Please specify" name="hearing_type_other" value={data.hearing_type_other} onChange={onChange} />
              )}
              <TextField label="Court proceedings number (not mandatory)" name="court_number" value={data.court_number} onChange={onChange} />
            </>
          )}
        </div>
      </div>

      <div>
        <SectionHeader number="17" title="Payment of fees" />
        <div className="space-y-5">
          <RadioField label="Have you or any other person paid any of your legal fees for this case?" name="fees_paid" value={data.fees_paid} onChange={onChange} options={[{value:"Yes",label:"Yes"},{value:"No",label:"No"}]} />
          {data.fees_paid === "Yes" && (
            <>
              <div className="p-3.5 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 space-y-3">
                <p>
                  Please provide proof of any legal fees paid for this matter, such as trust receipts, bank statements, or a signed cost agreement.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files)}
                />
                
                <div className="flex items-center gap-3 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-white border-amber-200 hover:bg-amber-100 text-amber-900 font-medium rounded-xl shadow-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 text-amber-700" />
                    Upload
                  </Button>
                  
                  {uploading && (
                    <span className="flex items-center gap-1.5 text-xs text-amber-700">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Uploading...
                    </span>
                  )}

                  {uploadError && (
                    <span className="flex items-center gap-1 text-xs text-red-650">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {uploadError}
                    </span>
                  )}
                </div>

                {data.fees_proof_attachment && (
                  <div className="p-2.5 bg-amber-100/50 border border-amber-200 rounded-lg flex items-center justify-between max-w-md mt-2">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full shrink-0" />
                      <a
                        href={data.fees_proof_attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold text-amber-900 hover:underline truncate font-semibold"
                      >
                        proof of payment attached to this application
                      </a>
                      {data.fees_proof_attachment_name && (
                        <span className="text-[10px] text-amber-700 truncate font-normal shrink-0">
                          ({data.fees_proof_attachment_name})
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-600 hover:text-red-800 hover:bg-amber-200/50 shrink-0"
                      onClick={removeAttachment}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              <TextField label="Name of the person who paid the legal fees" name="fee_payer_name" value={data.fee_payer_name} onChange={onChange} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField label="Relationship to you" name="fee_payer_relationship" value={data.fee_payer_relationship} onChange={onChange} />
                <TextField label="Amount paid" name="fee_amount_paid" value={data.fee_amount_paid} onChange={onChange} />
              </div>
              <TextAreaField label="If another person has paid previous fees, please outline why they cannot continue to pay:" name="fee_payer_reason" value={data.fee_payer_reason} onChange={onChange} />
            </>
          )}
        </div>
      </div>

      <div>
        <SectionHeader number="18" title="Your lawyer" />
        <div className="space-y-5">
          <TextField label="Who do you want as a lawyer?" name="preferred_lawyer" value={data.preferred_lawyer} onChange={onChange} />
          <TextField label="Firm's name and details" name="lawyer_firm_details" value={data.lawyer_firm_details} onChange={onChange} />
          <div className="p-4 bg-blue-50 rounded-lg text-sm text-gray-600">
            If you have a lawyer assisting you with this application, we recommend that you ask the lawyer to submit the application on your behalf.
          </div>
        </div>
      </div>

      <div>
        <SectionHeader number="19" title="Your legal problem" />
        <div className="space-y-5">
          <RadioField label="What type of law applies to your legal problem?" name="law_type" value={data.law_type} onChange={onChange} options={LAW_TYPE_OPTIONS} inline={false} />
          {data.law_type === "Criminal law" && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Criminal charges</label>
              <p className="text-xs text-gray-500">Search or open a category, then select every charge that applies.</p>
              <ChargeSelector value={Array.isArray(data.criminal_charges) ? data.criminal_charges : []} onChange={(charges) => onChange("criminal_charges", charges)} />
            </div>
          )}
          {data.law_type === "Civil law" && (
            <TextField label="Please specify (e.g. inquest, discrimination)" name="civil_law_specify" value={data.civil_law_specify} onChange={onChange} />
          )}
          <CheckboxGroup label="Are any of the following factors relevant to your legal problem?" name="relevant_factors" values={data.relevant_factors} onChange={onChange} options={FACTOR_OPTIONS} />
        </div>
      </div>
    </div>
  );
}
